/* Environments card: pure model. What the "Add environment" toggle shows and the per-row actions. */
import type { Environment, Project } from '@hangar/shared';
import type { RowAction } from '@/components/RowActions';

/** The environment id being edited inline, `new` for the add form under the table, or nothing. */
export type EditingTarget = string | 'new' | null;

export function addToggle(editing: EditingTarget): { expanded: boolean; icon: 'close' | 'add'; label: string; next: EditingTarget } {
  const expanded = editing === 'new';
  return { expanded, icon: expanded ? 'close' : 'add', label: expanded ? 'Close' : 'Add environment', next: expanded ? null : 'new' };
}

interface ActionsInput {
  /** Hidden for roles that cannot even see the controls (guests). */
  hidden: boolean;
  /** Why the actions are disabled (role, or the API lacks `environmentsEdit`), when they are. */
  reason: string | undefined;
  onEdit: () => void;
  onArchive: () => void;
}

/** Edit · Archive of one environment row. */
export function environmentActions({ hidden, reason, onEdit, onArchive }: ActionsInput): RowAction[] {
  if (hidden) return [];
  return [
    { key: 'edit', label: 'Edit', icon: 'edit', testId: 'env-edit', disabledReason: reason, onSelect: onEdit },
    { key: 'archive', label: 'Archive', icon: 'archive', testId: 'env-archive', destructive: true, disabledReason: reason, onSelect: onArchive },
  ];
}

/** Why editing is blocked: the caller's role first, else the missing API capability. */
export const editBlockReason = (can: { allowed: boolean; reason?: string }, unavailable: string | undefined): string | undefined => (can.allowed ? unavailable : can.reason);

/** The version guard of an environment write: its own etag, else the project's. */
export const environmentEtag = (env: Pick<Environment, 'etag'>, project: Pick<Project, 'etag'>): string | undefined => env.etag ?? project.etag;

/** The line of an archived environment: its reserved slug and what it used to accept. */
export const archivedSummary = (env: Pick<Environment, 'allowedBranchPatterns'>): string => `slug reserved · ${env.allowedBranchPatterns.join(', ') || 'any branch'}`;
